import { DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG, floorplanImagesRootGuid } from '@constants'
import { v4 } from 'uuid'
import useUploadProjectFile from '@editor/hooks/requests/useUploadProjectFile'
import { useMutation } from '@tanstack/react-query'
import { AI_CHAT_UPLOAD_META } from '../../utils'

/** A storey plan the user picked in the chat; `pdfPage` for a PDF. */
export interface PickedFloorplan {
  file: File
  pdfPage?: number
}

/**
 * Upload the storey plans (Folien) picked in the chat, one after another,
 * each named by its storey as the storey toolbar's upload does (the app
 * shows a plan on the storey of its name). The agent measures scale and
 * position afterwards, so they start with the toolbar's defaults.
 * `onStoreyUploaded` reports each stored plan, so a retry after a failed
 * upload sends only the storeys still missing instead of duplicating plans.
 */
const useChatFloorplanUpload = (
  onUploaded: () => void,
  onStoreyUploaded: (storey: string) => void,
) => {
  const { mutateAsync: uploadFile } =
    useUploadProjectFile<StoreyImageTransform>(floorplanImagesRootGuid)

  return useMutation(
    async (picked: [string, PickedFloorplan][]) => {
      for (const [storey, { file, pdfPage }] of picked) {
        await uploadFile({
          file,
          name: storey,
          additional_data: DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG,
          guid: v4(),
          parent_guid: floorplanImagesRootGuid,
          hierarchy_position: 0,
          type: 'file',
          pdfPage,
        })
        onStoreyUploaded(storey)
      }
    },
    // A failed upload is reported by `useUploadProjectFile`'s own onError.
    { meta: AI_CHAT_UPLOAD_META, onSuccess: onUploaded },
  )
}

export default useChatFloorplanUpload
